import { IntlMessageFormat } from 'intl-messageformat';
import { CATALOGS } from './catalogs/index.js';
import { DEFAULT_LOCALE } from './locales.js';
const isProduction = () => typeof process !== 'undefined' && process.env?.['NODE_ENV'] === 'production';
function splitId(id) {
    const sep = id.indexOf(':');
    return [id.slice(0, sep), id.slice(sep + 1)];
}
function lookupRaw(locale, scope, key) {
    const catalog = CATALOGS[locale];
    return catalog[scope]?.[key];
}
/**
 * Resolves the raw ICU pattern for `id` under `locale`, falling back to
 * `en` (§20.1 fallback chain: requested locale → en → the key itself). A
 * missing key throws in non-production so drift is caught before merge;
 * in production it degrades to the raw id rather than a blank/crash.
 */
function resolveRaw(locale, id) {
    const [scope, key] = splitId(id);
    const direct = lookupRaw(locale, scope, key);
    if (direct !== undefined)
        return direct;
    if (locale !== DEFAULT_LOCALE) {
        const fallback = lookupRaw(DEFAULT_LOCALE, scope, key);
        if (fallback !== undefined) {
            if (!isProduction()) {
                // eslint-disable-next-line no-console -- dev-only i18n drift warning, never runs in production
                console.warn(`[@xdocs/i18n] missing message "${id}" for locale "${locale}" — falling back to "${DEFAULT_LOCALE}"`);
            }
            return fallback;
        }
    }
    if (!isProduction()) {
        throw new Error(`[@xdocs/i18n] missing message id "${id}" (locale "${locale}")`);
    }
    return id;
}
const formatterCache = new Map();
function getFormatter(locale, id) {
    const cacheKey = `${locale}:${id}`;
    const cached = formatterCache.get(cacheKey);
    if (cached)
        return cached;
    const raw = resolveRaw(locale, id);
    const formatter = new IntlMessageFormat(raw, locale);
    formatterCache.set(cacheKey, formatter);
    return formatter;
}
/**
 * Core translation primitive: resolves `id` under `locale` through a cached
 * `IntlMessageFormat` instance (one per `(locale, id)`, §20.2) and formats
 * `vars` (ICU plural/select/number/date args). Locale is always an explicit
 * argument here — this is a leaf package with no ambient context; `@xdocs/ui`
 * binds it to `LocaleProvider` via `createTranslator`.
 */
export function translate(locale, id, vars) {
    const formatter = getFormatter(locale, id);
    const result = formatter.format(vars);
    if (result == null)
        return '';
    if (typeof result === 'string')
        return result;
    return Array.isArray(result) ? result.map(String).join('') : String(result);
}
/** Binds `translate` to a fixed locale — the shape `useI18n().t` wraps. */
export function createTranslator(locale) {
    return { locale, t: (id, vars) => translate(locale, id, vars) };
}
/** Returns the full raw catalog for a locale (debugging, `/styleguide`, tests). */
export function getCatalog(locale) {
    return CATALOGS[locale];
}
